import { Config } from '@/config';
import { ApiService } from '@/services/api.service';

// Only send filters that are actually set: null values and empty arrays are
// omitted so an unset dimension (e.g. `followed` after returning to "All")
// doesn't appear in the request at all — the backend's defaults apply.
const toSearchBody = (filters) => {
  const body = {};
  Object.entries(filters || {}).forEach(([key, value]) => {
    // `years` is the year chip's discrete pick (UI state) — the backend only
    // knows the fromYear/toYear overlap window derived from it.
    if (key === 'years') return;
    if (value == null) return;
    if (Array.isArray(value) && value.length === 0) return;
    body[key] = value;
  });
  return body;
};

class PreProjectsService {
  getPreProjects({ page = 1, pageSize = 25, sortingField = 'createdAt', sortingOrder = 'desc', filters = {} } = {}) {
    return ApiService.post(
      `${Config.api.tender.url}pre-projects/search?page=${page}&pageSize=${pageSize}&sortingField=${encodeURIComponent(
        sortingField,
      )}&sortingOrder=${sortingOrder}`,
      toSearchBody(filters),
    ).then((response) => {
      const data = response.data;
      if (Array.isArray(data)) {
        return {
          preProjects: data,
          availableYears: [],
          availableEmitters: [],
          pagination: { page: 1, totalCount: data.length, totalPages: 1 },
        };
      }
      return {
        preProjects: data.items || [],
        availableYears: data.availableYears || [],
        availableEmitters: data.availableEmitters || [],
        pagination: {
          page: data.page || page,
          totalCount: data.totalCount || 0,
          totalPages: data.totalPages || 0,
        },
      };
    });
  }

  // Custom (tenant-entered) pre-projects
  createPreProject(preProject) {
    return ApiService.post(`${Config.api.tender.url}pre-projects`, preProject).then((response) => response.data);
  }

  updatePreProject(preProjectId, preProject) {
    return ApiService.put(`${Config.api.tender.url}pre-projects/${preProjectId}`, preProject).then(
      (response) => response.data,
    );
  }

  deletePreProject(preProjectId) {
    return ApiService.delete(`${Config.api.tender.url}pre-projects/${preProjectId}`).then((response) => response.data);
  }

  importCsv(file, { fullFile = true, dryRun = false, reset = false } = {}) {
    const formData = new FormData();
    formData.append('file', file);
    const query = `?fullFile=${fullFile}&dryRun=${dryRun}&reset=${reset}`;
    return ApiService.post(`${Config.api.tender.url}pre-projects/import${query}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((response) => response.data);
  }

  // Follow (enabled: true) or unfollow (enabled: false) a pre-project.
  setFollowed(preProjectId, enabled) {
    return ApiService.put(`${Config.api.tender.url}pre-projects/${preProjectId}/follow`, { enabled }).then(
      (response) => response.data,
    );
  }

  // Share (enabled: true) or unshare (enabled: false) a pre-project with the
  // whole tenant, as a tender is shared from the inbox. Both are idempotent and
  // return the tenant's state for it ({ shared, sharedDate, sharedBy }).
  setShared(preProjectId, enabled) {
    const action = enabled ? 'share' : 'unshare';
    return ApiService.put(`${Config.api.tender.url}pre-projects/${preProjectId}/${action}`).then(
      (response) => response.data,
    );
  }
}

export const preProjectsService = new PreProjectsService();
